import { t } from '~i18n';
import styles from './DeviceMapOverlay.module.css';
import { Button } from '@ixicare/ui';
import {
  Beacon,
  Device,
  DeviceType,
  ECoordsSrid,
  Gateway,
  ProfileType,
  TUpdateCoords,
  getIBeaconVendor,
  getSrdBeaconId,
} from '@ixicare/resources';
import { useMutation } from '@tanstack/react-query';
import { DBG } from '~modules/devices/components/DeviceHealthTable/types';
import { OnMarkerInsertEvent } from '~components/Map/DynamicMap';
import { Spacer } from '~components/Utils/Spacer';
import { useStores } from '~/store/utils';
import { useDeviceArchive } from '~modules/sites/hooks/useDeviceArchive';
import { DeleteDeviceModal } from '~modules/devices/components/DeviceModals/DeleteDeviceModal';

const DeviceMapOverlay = ({
  device,
  type,
  onClose,
  onRefresh,
  onMoveStart,
  onMoveEnd,
  isMoving,
  newLocation,
  isEditing,
  onEditStart,
  coordSrid = ECoordsSrid.Indoor,
}) => {
  const { notifications, auth } = useStores();

  const isEmployee = auth.userType === ProfileType.WatcherrEmployee;

  const { devicesToArchive, setDevicesToArchive, deleteMutation } = useDeviceArchive({
    onClose,
    onRefresh,
    notifications,
  });

  const srdBeaconId = getSrdBeaconId(device.macAddress);

  const moveMutation = useMutation({
    mutationFn: () => updateDevice(type, device, newLocation, coordSrid),
    onSuccess: () => {
      onClose();
      onMoveEnd();
      onRefresh();
    },
  });

  return (
    <div className={styles.DeviceMapOverlay}>
      <button className={styles.Close} onClick={onClose}>
        <i className="icon-cross" />
      </button>
      <dl>
        <dt>{t('devices.table.deviceType')}</dt>
        {type === 'WALL_MOUNTED_DISTRESS_BUTTON' ? (
          <dd>{t(`device.types.${device.subType ? `${device.subType.toLowerCase()}` : `regular_button`}`)}</dd>
        ) : (
          <dd>{t(`device.types.${type.toLowerCase()}`)}</dd>
        )}
      </dl>
      {/* A beacon we don't make has no address of its own: what it advertises is its identity, and
          the address it is stored under is one we made up for it. Showing that would be a lie. */}
      {device.ibeaconUuid ? (
        <>
          <dl>
            <dt>{t('device.form.beaconSource')}</dt>
            <dd>{getIBeaconVendor(device.ibeaconUuid)?.name ?? t('device.types.beacon')}</dd>
          </dl>
          <dl>
            <dt>{t('device.form.ibeaconMajor')}</dt>
            <dd>{device.ibeaconMajor}</dd>
          </dl>
          <dl>
            <dt>{t('device.form.ibeaconMinor')}</dt>
            <dd>{device.ibeaconMinor}</dd>
          </dl>
        </>
      ) : srdBeaconId !== undefined ? (
        <>
          <dl>
            <dt>{t('device.form.beaconSource')}</dt>
            <dd>{t('device.form.beaconSourceSrd')}</dd>
          </dl>
          <dl>
            <dt>{t('device.form.srdBeaconId')}</dt>
            <dd>{srdBeaconId}</dd>
          </dl>
        </>
      ) : (
        <dl>
          <dt>{t('devices.table.macAddress')}</dt>
          <dd>{device.macAddress}</dd>
        </dl>
      )}

      <dl>
        <dt>{t('devices.table.description')}</dt>
        <dd>{device.description ?? '-'}</dd>
      </dl>
      <Spacer size={20} />

      {isMoving ? (
        <>
          <Button
            isBlock
            buttonType="success"
            loading={moveMutation.isPending}
            disabled={!newLocation}
            onClick={() => moveMutation.mutate()}
          >
            {newLocation?.zoneUuid
              ? t('beamSettings.movingIndicator.moveToRoom', {
                  room: newLocation.zoneName,
                })
              : t('beamSettings.movingIndicator.moveOutsideOfRoom')}
          </Button>
          <Spacer size={5} />
          <Button isBlock buttonType="error" onClick={onMoveEnd}>
            {t('global.cancel')}
          </Button>
        </>
      ) : (
        <>
          <Button isBlock buttonType="success" disabled={isEditing} onClick={onEditStart}>
            {t('site.map.deviceMapOverlay.edit')}
          </Button>
          <Spacer size={5} />
          <Button isBlock buttonType="primary" disabled={isMoving} onClick={onMoveStart}>
            {t('site.map.deviceMapOverlay.move')}
          </Button>
          <Spacer size={5} />
          <Button
            isBlock
            loading={deleteMutation.isPending}
            buttonType="error"
            onClick={() => setDevicesToArchive([{ ...device, type }])}
          >
            {t('site.map.deviceMapOverlay.delete')}
          </Button>
        </>
      )}
      {devicesToArchive.length > 0 && (
        <DeleteDeviceModal
          devices={devicesToArchive}
          showHardDelete={isEmployee}
          onClose={() => setDevicesToArchive([])}
          onDelete={(hardDelete) => deleteMutation.mutate(hardDelete)}
          isPending={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

const updateDevice = async (
  type: DeviceType,
  device: DBG,
  newLocation: OnMarkerInsertEvent,
  coordSrid: ECoordsSrid = ECoordsSrid.Indoor
) => {
  const installationCoords: TUpdateCoords | undefined =
    newLocation &&
    ({
      x: newLocation.x,
      y: newLocation.y,
      srid: coordSrid,
    } as TUpdateCoords);

  if (type === DeviceType.BEACON) {
    await Beacon.patchBeacon(device.id, {
      zoneId: newLocation?.zoneUuid,
      planeId: newLocation?.planeId,
      installationCoords,
      distanceToLeftWall: 0,
      distanceToRightWall: 0,
    });
  }
  if (type === DeviceType.GATEWAY) {
    await Gateway.patchGateway(device.id, {
      zoneId: newLocation?.zoneUuid,
      planeId: newLocation?.planeId,
      installationCoords,
    });
  }
  if (type === DeviceType.WALL_MOUNTED_DISTRESS_BUTTON) {
    await Device.patchDevice(device.id, {
      installationCoords,
      installationZoneId: newLocation?.zoneUuid,
      planeId: newLocation?.planeId,
    });
  }
};

export default DeviceMapOverlay;
